import * as d3 from "d3";
import {
  dayGranularity,
  GranularityProperty,
  monthGranularity,
  quarterGranularity,
  weekGranularity,
  yearGranularity,
} from "../../../models/types/balanceGroupingTypes";
import { Scales } from "./scales";
import { countPeriods } from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";

const Y_TICK_COUNT = 4;

export class Axis {
  private balanceGraph: any;
  private xScale: d3.ScaleTime<number, number, never>;
  private yScale: d3.ScaleLinear<number, number, never>;
  private maxYAxisValue: number;
  private minYAxisValue: number;
  private granularity: GranularityProperty;
  private readonly isMobile: boolean;

  constructor(
    balanceGraph: any,
    xScale: d3.ScaleTime<number, number, never>,
    yScale: d3.ScaleLinear<number, number, never>,
    isMobile: boolean,
  ) {
    this.balanceGraph = balanceGraph;
    this.xScale = xScale;
    this.yScale = yScale;
    this.isMobile = isMobile;
  }

  drawAxis(
    innerHeight: number,
    startDate: Date,
    endDate: Date,
    granularity: GranularityProperty,
    minYAxisValue: number,
    maxYAxisValue: number,
    tickValues?: Date[],
    xLabel?: string,
    yLabel?: string,
  ) {
    this.granularity = granularity;
    this.minYAxisValue = minYAxisValue;
    this.maxYAxisValue = maxYAxisValue;

    // tickValues = tickValues.map((value) => new Date(value).getTime());

    this.balanceGraph
      .append("g")
      .attr("id", "x-axis")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${innerHeight})`)
      .style("stroke-width", "0.5px")
      .call(this.createXAxis(startDate, endDate, tickValues))
      .selectAll("text")
      .style("text-anchor", "middle");

    this.balanceGraph
      .append("g")
      .attr("id", "y-axis")
      .attr("class", "y-axis")
      .style("stroke-width", "0.5px")
      .call(this.createYAxis()); // left: text will be shown left of the line\

    if (xLabel) {
      this.addXAxisLabel(xLabel);
    }

    if (yLabel) {
      this.addYAxisLabel(yLabel);
    }
  }

  redrawAxis(
    innerHeight: number,
    startDate: Date,
    endDate: Date,
    granularity: GranularityProperty,
    minYAxisValue: number,
    maxYAxisValue: number,
    tickValues?: Date[],
  ) {
    this.granularity = granularity;
    this.minYAxisValue = minYAxisValue;
    this.maxYAxisValue = maxYAxisValue;

    this.balanceGraph
      .select("#x-axis")
      .attr("transform", `translate(0,${innerHeight})`)
      .style("stroke-width", "0.5px")
      .call(this.createXAxis(startDate, endDate, tickValues))
      .selectAll("text")
      .style("text-anchor", "middle")
      .transition()
      .ease(d3.easePolyInOut)
      .duration(500); // Adjust text-anchor for proper alignment

    this.redrawYAxis(innerHeight);
  }

  redrawYAxis(innerHeight: number) {
    this.balanceGraph
      .select("#y-axis")
      .transition()
      .ease(d3.easePolyInOut)
      .duration(500)
      .style("stroke-width", "0.5px")
      .call(this.createYAxis());
  }

  private createXAxis(startDate: Date, endDate: Date, tickValues: Date[]) {
    const xAxis = d3.axisBottom(this.xScale).tickFormat(this.getTickFormat(true));

    tickValues = this.filterTickValues(tickValues);

    // calculate the number of ticks to remove from tickValues
    const maxTicks = this.getTickCount();
    if (tickValues && tickValues.length > maxTicks) {
      const step = Math.ceil(tickValues.length / maxTicks);
      tickValues = tickValues.filter((_, index) => index % step === 0);
    }

    xAxis.tickValues(tickValues);

    return xAxis;
  }

  // temporarily fix for label overlap (always remove second and second last)
  private filterTickValues(tickValues: Date[]): Date[] {
    if (!tickValues || tickValues.length <= 4) {
      return tickValues;
    }
    return tickValues.filter((_, i, arr) => i !== 1 && i !== arr.length - 2);
  }

  private getTickFrequency(startDate: Date, endDate: Date): number {
    const tickType = this.getTickType();
    const intervals = tickType.count(startDate, endDate);
    const ticks = this.getTickCount();

    const step = Math.ceil(intervals / ticks);

    if (this.granularity === quarterGranularity) {
      if (step > 4) {
        return 6;
      }
      return 3;
    }
    return step;
  }

  private getTickCount(): number {
    const ticks = {
      mobile: {
        day: 7,
        week: 6,
        month: 6,
        quarter: 6,
        year: 6,
      },
      desktop: {
        day: 7,
        week: 10,
        month: 12,
        quarter: 6,
        year: 6,
      },
    };

    return ticks[this.isMobile ? "mobile" : "desktop"][this.granularity];
  }

  private getTickFormat(singleLine = true): (d: Date, i: number) => string {
    switch (this.granularity) {
      case dayGranularity:
        return d3.timeFormat("%e%b");
      case weekGranularity:
        if (singleLine) {
          return d3.timeFormat("Wk%V");
        } else {
          return (date: Date): string => {
            const weekNumber = d3.timeFormat("Wk%V")(date);
            const dayMth = d3.timeFormat("%e%b")(date);
            return `${weekNumber}|${dayMth}`;
          };
        }
      case monthGranularity:
        return d3.timeFormat("%e%b");
      case quarterGranularity:
        if (singleLine) {
          return (date: Date): string => {
            const quarter = Math.floor(date.getMonth() / 3 + 1);
            const year = date.getFullYear().toString().slice(-2);
            return `${year}Q${quarter}`;
          };
        } else {
          return (date: Date): string => {
            if (date.getMonth() % 3 === 2) {
              const quarter = Math.floor(date.getMonth() / 3 + 1);
              const year = date.getFullYear().toString().slice(-2);
              const dayMth = d3.timeFormat("%e%b")(date);
              return `${year}Q${quarter}|${dayMth}`;
            }
            return "";
          };
        }
      case yearGranularity:
        if (singleLine) {
          return d3.timeFormat("%Y");
        } else {
          return (date: Date): string => {
            const year = date.getFullYear();
            const dayMth = d3.timeFormat("%e%b")(date);
            return `${year}|${dayMth}`;
          };
        }
      default:
        return d3.timeFormat("%b %Y");
    }
  }

  private getTickType(): d3.CountableTimeInterval {
    switch (this.granularity) {
      case dayGranularity:
        return d3.timeDay;
      case weekGranularity:
        return d3.timeWeek;
      case monthGranularity:
        return d3.timeMonth;
      case quarterGranularity:
        return d3.timeMonth;
      case yearGranularity:
        return d3.timeYear;
      default:
        return d3.timeMonth;
    }
  }

  private getNumberOfIntervals(startDate: Date, endDate: Date): number {
    return countPeriods(startDate, endDate, this.granularity);
  }

  private createYAxis() {
    let tickCount = Y_TICK_COUNT;
    if (this.maxYAxisValue === this.minYAxisValue) {
      tickCount = 1;
    }

    return d3.axisLeft(this.yScale).ticks(tickCount).tickFormat(d3.format(".3s"));
  }

  addXAxisLabel(label: string) {
    this.balanceGraph
      .append("text")
      .attr("class", "x-axis-title")
      .attr("dy", "1px")
      .attr("dx", "1px")
      .style("text-anchor", "middle")
      .style("font-size", "11px")
      .attr("fill", "#c6c6c6")
      .text(label);
  }

  addYAxisLabel(label: string) {
    this.balanceGraph
      .append("text")
      .attr("class", "y-axis-title")
      .attr("dy", "-10px")
      .attr("dx", "-20px")
      .style("text-anchor", "middle")
      .style("font-size", "11px")
      .attr("fill", "#c6c6c6")
      .text(label);
  }

  highlightXAxisTick(date: number) {
    this.balanceGraph.selectAll(".x-axis text").style("font-weight", "normal");
    const xPosition = this.xScale(new Date(date * 1000));
    this.balanceGraph
      .selectAll(".x-axis .tick")
      .each((d: Date, i: number, nodes: SVGGElement[]) => {
        if (Math.abs(this.xScale(d) - xPosition) < 5) {
          d3.select(nodes[i]).select("text").style("font-weight", "bold");
        }
      });
  }

  resetXAxisHighlight() {
    this.balanceGraph.selectAll(".x-axis text").style("font-weight", "normal");
  }
}
